import * as React from 'react';
import './_TabDetails.scss';
import { EBoardSetting, ITabDetailsProps } from './types';
import { ESettingsSectionType } from '../../../../../../base/components/SettingsSection/types';
import { CLASS_BOARD_DETAILS, CLASS_BOARD_DETAILS_READ_ONLY } from '../../../../constants';
import { LinksToCardsSection } from '../../../LinksToCards/components/LinksToCardsSection/LinksToCardsSection';
import {
    EmailNotificationsSection
} from '../../../Notifications/components/EmailNotificationsSection/EmailNotificationsSection';
import { TimeZoneSection } from '../../../TimeZone/components/TimeZoneSection/TimeZoneSection';
import { BoardEmailSection } from '../../../Email/components/BoardEmailSection/BoardEmailSection';
import { CardPropertiesSection } from '../../../CardProperties/components/CardPropertiesSection/CardPropertiesSection';
import {
    CustomPropertiesSection
} from '../../../CardCustomProperties/components/CustomPropertiesSection/CustomPropertiesSection';
import { EstimateTimeSection } from '../../../EstimateTime/components/EstimateTimeSection/EstimateTimeSection';
import {
    PushNotificationsSectionHOC
} from '../../../Notifications/hocs/PushNotificationsSectionHOC/PushNotificationsSectionHOC';
import { TagsSection } from '../../../Tags/components/TagsSection/TagsSection';
import { PwcImportSection } from '../../../PwcImport/components/PwcImportSection/PwcImportSection';
import { DatesSection } from '../../../Dates/components/DatesSection/DatesSection';
import {
    BoardImportSection
} from 'app/view/react_components/aside_panel/boardDetails/details/ImportBoard/components/BoardImportSection/BoardImportSection';
import { ColorsSection } from '../../../Colors/components/ColorsSection/ColorsSection';
import { RolesSectionHOC } from '../../../Roles/hocs/RolesSectionHOC/RolesSectionHOC';
import { CardAgingSectionHOC } from '../../../CardAging/hocs/CardAgingSectionHOC/CardAgingSectionHOC';
import { PrefixesSectionHOC } from '../../../CardNumber/hocs/PrefixesSectionHOC/PrefixesSectionHOC';
import { StatusListsSectionsHOC } from '../../../StatusLists/hocs/StatusListsSectionsHOC/StatusListsSectionsHOC';
import { GanttSectionHOC } from '../../../ShowOnGantt/hocs/GanttSectionHOC/GanttSectionHOC';
import {
    ArchiveOnDeletePanelSectionHOC
} from '../../../ArchiveOnDelete/hocs/ArchiveOnDeletePanelSectionHOC/ArchiveOnDeletePanelSectionHOC';
import { Icon } from 'kui';
import { BOARD_SETTINGS_EMPTY_TEXT, BOARD_SETTINGS_EMPTY_TITLE } from './constants';
import { Info } from '../../../Info/components/Info/Info';
import { AsidePanelContext } from '../../../../../asidePanel/components/AsidePanel/constants';
import { AnalyzeRender } from '../../../../../../helpers/memoizeHelper';
import { WebhooksSectionHOC } from '../../../Webhooks/hocs/WebhooksSectionHOC/WebhooksSectionHOC';
// @ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`TabDetails`) : null;

export function TabDetails ({
    boardId,
    isNewFeature,
    isBoardSplitSettings,
    sectionType,
    onSubTabChange,
}: ITabDetailsProps) {
    if (analyzeRender) analyzeRender.call(boardId);
    const isReadonly = sectionType === ESettingsSectionType.READONLY;
    const className = CLASS_BOARD_DETAILS;
    const classNameReadonly = isReadonly ? CLASS_BOARD_DETAILS_READ_ONLY : '';
    const classEmptyDummy = className + '__empty-dummy';

    const { subTab } = React.useContext(AsidePanelContext);

    React.useEffect(() => {
        if (!isNewFeature || !subTab) return;
        onSubTabChange();
    }, [isNewFeature, subTab]);

    return (<div className={`
            ${className}
            ${classNameReadonly}
            ${isBoardSplitSettings ? className + '--split-settings' : ''}
        `}>
        {isNewFeature && !subTab &&
            <div className={classEmptyDummy + '-wrapper'}>
                <div className={classEmptyDummy}>
                    <Icon xlink={'settings'} size={24} />
                    <span className={classEmptyDummy + '-title'}>{BOARD_SETTINGS_EMPTY_TITLE}</span>
                    <span className={classEmptyDummy + '-text'}>{BOARD_SETTINGS_EMPTY_TEXT}</span>
                </div>
            </div>
        }

        {(subTab === EBoardSetting.INFO || !isBoardSplitSettings) && <Info boardId={boardId} isReadonly={isReadonly} />}

        {(subTab === EBoardSetting.EMAIL_NOTIFICATIONS || !isBoardSplitSettings) && <EmailNotificationsSection/>}
        {(subTab === EBoardSetting.APP_NOTIFICATIONS || !isBoardSplitSettings) && <PushNotificationsSectionHOC boardId={boardId}/>}

        {(subTab === EBoardSetting.CARD_PREFIXES || !isBoardSplitSettings) && <PrefixesSectionHOC boardId={boardId}/>}
        {(subTab === EBoardSetting.CARD_PROPERTIES || !isBoardSplitSettings) && <CardPropertiesSection/>}
        {(subTab === EBoardSetting.CUSTOM_PROPERTIES || !isBoardSplitSettings) && <CustomPropertiesSection/>}
        {(subTab === EBoardSetting.COLOR_LABELS || !isBoardSplitSettings) && <ColorsSection/>}
        {(subTab === EBoardSetting.TAGS || !isBoardSplitSettings) && <TagsSection/>}
        {(subTab === EBoardSetting.ROLES || !isBoardSplitSettings) && <RolesSectionHOC/>}
        {(subTab === EBoardSetting.DATES_TIME || !isBoardSplitSettings) && <DatesSection />}

        {(subTab === EBoardSetting.CARD_AGING || !isBoardSplitSettings) && <CardAgingSectionHOC boardId={boardId}/>}
        {(subTab === EBoardSetting.GANTT || !isBoardSplitSettings) && <GanttSectionHOC/>}
        {(subTab === EBoardSetting.ARCHIVE_UPON_DELETION || !isBoardSplitSettings) && <ArchiveOnDeletePanelSectionHOC />}
        {(subTab === EBoardSetting.STATUS_MAPPING || !isBoardSplitSettings) && <StatusListsSectionsHOC />}
        {(subTab === EBoardSetting.ESTIMATE || !isBoardSplitSettings) && <EstimateTimeSection />}
        {(subTab === EBoardSetting.WEBHOOKS || !isBoardSplitSettings) && <WebhooksSectionHOC boardId={boardId} />}

        {(subTab === EBoardSetting.LINKS_PREVIEW || !isBoardSplitSettings) && <LinksToCardsSection />}
        {(subTab === EBoardSetting.TIME_ZONE || !isBoardSplitSettings) && <TimeZoneSection />}
        {(subTab === EBoardSetting.BOARD_EMAIL || !isBoardSplitSettings) && <BoardEmailSection />}

        {(subTab === EBoardSetting.IMPORT_PWC || !isBoardSplitSettings) && <PwcImportSection />}
        {(subTab === EBoardSetting.IMPORT || !isBoardSplitSettings) && <BoardImportSection />}
    </div>)
}
